import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/api/dashboard';
import type { IndexTile } from '@/api/types';
import { krw, krwShort, pct, pnlColor } from '@/lib/format';
import { cn } from '@/lib/cn';
import { LiveStatus } from '@/components/LiveStatus';

// Sticky header band: live NAV/cash/day-P&L from /api/portfolio/header +
// KOSPI/KOSDAQ daily close tape from /api/market/indices. LiveStatus is
// real SSE.
export function Header() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolioHeader'],
    queryFn: dashboardApi.portfolioHeader,
    refetchInterval: 30_000,
  });

  const { data: indices } = useQuery({
    queryKey: ['marketIndices'],
    queryFn: dashboardApi.marketIndices,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <header className="sticky top-0 z-30 bg-bg/85 backdrop-blur-md border-b border-[color:var(--border)]">
      <div className="px-4 lg:px-6 py-3 flex items-center gap-3 lg:gap-6 flex-wrap">
        {/* NAV hero */}
        <div className="flex items-baseline gap-3">
          <div>
            <div className="label">NAV</div>
            <div className="font-mono text-xl lg:text-2xl font-bold grad-text">
              {isLoading ? '—' : isError ? '?' : krwShort(data?.nav ?? 0)}
            </div>
          </div>
          <div className="hidden sm:block">
            <div className="label flex items-center gap-1">
              Cash {data?.cashStale && <span className="text-kam-amber" title="Cash value is stale — KIS call failed">●</span>}
            </div>
            <div className="font-mono text-sm text-text-dim">
              {data ? krwShort(data.cash) : '—'}
            </div>
          </div>
        </div>

        {/* Day P&L chip */}
        {data && (
          <div className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-chip text-xs font-semibold border',
            data.dayPnl >= 0
              ? 'bg-kam-green-soft text-kam-green border-kam-green/25'
              : 'bg-kam-red-soft text-kam-red border-kam-red/25',
          )}>
            <span className="font-mono">{krw(data.dayPnl, { sign: true })}</span>
            <span className={cn('opacity-80 font-mono', pnlColor(data.dayPnlPct))}>
              ({pct(data.dayPnlPct)})
            </span>
          </div>
        )}

        <div className="flex-1" />

        {/* Market tape — KOSPI/KOSDAQ daily close (KODEX 200 / KODEX 코스닥150 proxies) */}
        <div className="hidden md:flex items-center gap-2">
          <IndexTileView label="KOSPI" tile={indices?.kospi} />
          <IndexTileView label="KOSDAQ" tile={indices?.kosdaq} />
        </div>

        {/* SSE connection indicator */}
        <div className="hidden md:flex items-center">
          <LiveStatus />
        </div>
      </div>
    </header>
  );
}

function IndexTileView({ label, tile }: { label: string; tile: IndexTile | undefined }) {
  if (!tile || tile.value == null) {
    return (
      <div className="market-tile flex items-center gap-2 px-2.5 py-1.5 bg-surface-2 border border-[color:var(--border)] rounded-[10px] text-xs"
           title={`${label} — no data yet`}>
        <span className="text-text-faint">{label}</span>
        <span className="font-mono text-text-faint">—</span>
      </div>
    );
  }
  const chg = tile.chgPct;
  const titleParts = [
    `${label} via ${tile.source ?? 'KODEX proxy'}`,
    tile.asOf && `close ${tile.asOf}`,
  ].filter(Boolean).join(' · ');
  return (
    <div
      className="market-tile flex items-center gap-2 px-2.5 py-1.5 bg-surface-2 border border-[color:var(--border)] rounded-[10px] text-xs"
      title={titleParts}
    >
      <span className="text-text-faint">{label}</span>
      <span className="font-mono">
        {tile.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      {chg != null && (
        <span className={cn('font-mono', chg >= 0 ? 'text-kam-green' : 'text-kam-red')}>
          {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
        </span>
      )}
    </div>
  );
}
